import React, {
    createContext,
    Dispatch,
    Fragment,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { canProceedFromConversation, countPersonaMessages, getInteractionLimits, resolveCompletionThreshold, shouldOpenIntermediateProceedWindow } from '../../../helpers/misc.helper';

import { useCompleteConversation, useGetChats, useGetPersonaDetails, useGetScenarioWithPersonas } from '../../../hooks/mbts-ux';

import { IChatMessage } from '../../../types/misc.interface';
import { IMBTSPersonaWithChildren, IMBTSScenarioWithPersonasAndStatuses } from '../../../types/services.interfaces/mbts-ux';

import { ChatCompletionWindow } from './ChatCompletionWindow';
import { ChatIntermediateProceedWindow } from './ChatIntermediateProceedWindow';

export type TMBTSMessageThreshold = 'level1' | 'level2' | null;
export type TMBTSNextOperation = 'next-persona' | 'analysis' | null;

interface IMBTSConversationContext {
    scenarioId: string | null;
    personaId: string | null;
    conversationId: string | null;
    personaDetails: IMBTSPersonaWithChildren;
    scenarioDetails: IMBTSScenarioWithPersonasAndStatuses | null;
    isLoadingChats: boolean;
    chats: IChatMessage[];
    setChats: Dispatch<SetStateAction<IChatMessage[]>>;
    isCompletionWindowVisible: boolean;
    setIsCompletionWindowVisible: Dispatch<SetStateAction<boolean>>;
    messageThreshold: TMBTSMessageThreshold;
    setMessageThreshold: Dispatch<SetStateAction<TMBTSMessageThreshold>>;
    continueMode: boolean;
    setContinueMode: Dispatch<SetStateAction<boolean>>;
    isProcessingOverview: boolean;
    nextOperation: TMBTSNextOperation;
    nextPersonaDetails: { personaId: string } | null;
    interactionLimits: { eligibility: number; level1: number; level2: number };
    evaluateMessageThreshold: (messages: IChatMessage[]) => TMBTSMessageThreshold;
    isHandleNextDisabled: () => boolean;
    handleNext: () => void;
    stayInConversation: () => void;
};

const MBTSConversationContext = createContext<IMBTSConversationContext | null>(null);

export const useMBTSConversation = () => {
    const context = useContext(MBTSConversationContext);

    if (!context) {
        throw new Error('useMBTSConversation must be used within MBTSConversationWrapper');
    };

    return context;
};

interface IMBTSConversationWrapperProps {
    scenarioId: string | null;
    personaId: string | null;
    conversationId: string | null;
    children: ReactNode;
};

export const MBTSConversationWrapper = (props: IMBTSConversationWrapperProps) => {
    const { scenarioId, personaId, conversationId, children } = props;

    const hasHydratedChatsRef = useRef(false);
    const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const continueModeRef = useRef(false);
    const isCompletionWindowVisibleRef = useRef(false);

    const [chats, setChats] = useState<IChatMessage[]>([]);
    const [isCompletionWindowVisible, setIsCompletionWindowVisible] = useState(false);
    const [messageThreshold, setMessageThreshold] = useState<TMBTSMessageThreshold>(null);
    const [continueMode, setContinueMode] = useState(false);
    const [isProcessingOverview, setIsProcessingOverview] = useState(false);
    const [isIntermediateProcessWindowVisible, setIsIntermediateProcessWindowVisible] = useState(false);
    const [nextOperation, setNextOperation] = useState<TMBTSNextOperation>(null);
    const [nextPersonaDetails, setNextPersonaDetails] = useState<{ personaId: string } | null>(null);

    const { data: personaDetails, isLoading: isLoadingPersonaDetails } = useGetPersonaDetails(scenarioId, personaId);
    const { data: scenarioDetails } = useGetScenarioWithPersonas(scenarioId);
    const { data: fetchedChats, isLoading: isLoadingChats } = useGetChats(conversationId);
    const { mutate: completeConversation } = useCompleteConversation();

    const interactionLimits = getInteractionLimits(personaDetails ?? null);

    useEffect(() => {
        continueModeRef.current = continueMode;
    }, [continueMode]);

    useEffect(() => {
        isCompletionWindowVisibleRef.current = isCompletionWindowVisible;
    }, [isCompletionWindowVisible]);

    const clearCompletionTimer = useCallback(() => {
        if (completionTimerRef.current) {
            clearTimeout(completionTimerRef.current);
            completionTimerRef.current = null;
        };
    }, []);

    const scheduleCompletionWindow = useCallback(() => {
        if (completionTimerRef.current || isCompletionWindowVisibleRef.current) return;

        completionTimerRef.current = setTimeout(() => {
            completionTimerRef.current = null;
            setIsCompletionWindowVisible(true);
        }, 5000);
    }, []);

    const evaluateMessageThreshold = useCallback((messages: IChatMessage[]): TMBTSMessageThreshold => {
        const personaMessageCount = countPersonaMessages(messages);
        const limits = getInteractionLimits(personaDetails ?? null);
        const threshold = resolveCompletionThreshold(personaMessageCount, limits, continueModeRef.current);

        if (!threshold) return null;

        setMessageThreshold(threshold);
        scheduleCompletionWindow();
        return threshold;
    }, [personaDetails, scheduleCompletionWindow]);

    useEffect(() => {
        if (hasHydratedChatsRef.current || !fetchedChats || !personaDetails) return;
        hasHydratedChatsRef.current = true;

        if (!fetchedChats.length) return;

        const hydrated = fetchedChats.map((chat) => ({
            by: chat.source,
            message: chat.message,
            timestamp: chat.created_at,
        }));
        setChats(hydrated);
        evaluateMessageThreshold(hydrated);
    }, [evaluateMessageThreshold, fetchedChats, personaDetails]);

    useEffect(() => {
        if (!personaDetails || !scenarioDetails) return;

        if (scenarioDetails.personas.length > 1) {
            const idx = scenarioDetails.personas.findIndex(p => p.id === personaDetails.id);
            if (idx === -1) return;

            if (idx < scenarioDetails.personas.length - 1) {
                setNextOperation('next-persona');
                const nxtPersona = scenarioDetails.personas[idx + 1];
                if (nxtPersona.status !== 'completed') {
                    setNextPersonaDetails({ personaId: nxtPersona.id });
                };
            } else {
                setNextOperation('analysis');
            };
        } else {
            setNextOperation('analysis');
        };
    }, [personaDetails, scenarioDetails]);

    useEffect(() => {
        return () => {
            clearCompletionTimer();
        };
    }, [clearCompletionTimer]);

    const isHandleNextDisabled = (): boolean => {
        if (!scenarioId || !conversationId || !nextOperation) return true;
        return !canProceedFromConversation(countPersonaMessages(chats), interactionLimits);
    };

    const handleNext = () => {
        if (isHandleNextDisabled() || !conversationId || !nextOperation || !scenarioId) return;

        if (shouldOpenIntermediateProceedWindow(countPersonaMessages(chats), interactionLimits)) {
            setIsIntermediateProcessWindowVisible(true);
        } else {
            completeConversation({
                conversationId,
                scenarioId,
                setIsProcessing: setIsProcessingOverview,
                nextOperation,
                nextPersonaDetails,
            });
        };
    };

    const stayInConversation = () => {
        continueModeRef.current = true;
        clearCompletionTimer();
        setContinueMode(true);
        setIsCompletionWindowVisible(false);
        setMessageThreshold('level1');
    };

    if (isLoadingPersonaDetails && !personaDetails) {
        return (
            <div className='d-flex flex-column align-items-center justify-content-center gap-sm' style={{ flex: 1 }}>
                <div className='list-loader' />
                <p className='text-medium color--txt-secondary'>Loading persona details</p>
            </div>
        );
    };

    if (!isLoadingPersonaDetails && !personaDetails) {
        return (
            <p className='text-lg text-semibold color--txt-tertiary text-center' style={{ margin: 'auto' }}>
                No persona details found!
            </p>
        );
    };

    if (!personaDetails) return null;

    const value: IMBTSConversationContext = {
        scenarioId,
        personaId,
        conversationId,
        personaDetails,
        scenarioDetails: scenarioDetails ?? null,
        isLoadingChats,
        chats,
        setChats,
        isCompletionWindowVisible,
        setIsCompletionWindowVisible,
        messageThreshold,
        setMessageThreshold,
        continueMode,
        setContinueMode,
        isProcessingOverview,
        nextOperation,
        nextPersonaDetails,
        interactionLimits,
        evaluateMessageThreshold,
        isHandleNextDisabled,
        handleNext,
        stayInConversation,
    };

    return (
        <MBTSConversationContext.Provider value={value}>
            <Fragment>
                <ChatCompletionWindow
                    visible={isCompletionWindowVisible}
                    scenarioId={scenarioId ?? null}
                    conversationId={conversationId ?? ''}
                    messageThreshold={messageThreshold}
                    nextOperation={nextOperation}
                    nextPersonaDetails={nextPersonaDetails}
                    onStayInConversation={stayInConversation}
                />

                <ChatIntermediateProceedWindow
                    visible={isIntermediateProcessWindowVisible}
                    setVisible={setIsIntermediateProcessWindowVisible}
                    recommMsgs={interactionLimits.level1}
                    scenarioId={scenarioId ?? null}
                    conversationId={conversationId ?? ''}
                    nextOperation={nextOperation}
                    nextPersonaDetails={nextPersonaDetails}
                />

                {children}
            </Fragment>
        </MBTSConversationContext.Provider>
    );
};
